import {
	WebGLRenderer,
	LinearSRGBColorSpace,
	Scene,
	Group,
	Raycaster,
	Quaternion,
	Vector2,
	Vector3,
	PerspectiveCamera,
	RepeatWrapping,
	AudioListener,
	Color,
	TOUCH,
} from 'three';
import { audioLoader, textureLoader } from './instances.js';
import { MapControls } from 'three-utils';
import { createSpaceBackground } from './space-background.js';
import { captureIfRequested, requestScreenshot } from './screenshot.js';
import { setTextEdge } from 'imperial-lib';
import { showFovText, hideFovText } from './fov-text.js';
import { xrSettings } from './xr-settings.js';
import { CurvedHud } from './curved-hud.js';

// HUD panel in VR. XR camera space is METRES. The 2D layout put the panel at
// ~640 units = 640 m with zero stereo depth, drawn over planets 2–5 m away →
// vergence/occlusion conflict ("makes me feel sick close to a planet", user
// 2026-10-10), and every button sat at the end of a 640 m ray. Standard pattern
// instead: 1.5 m in front of the eyes (nearer than anything it can overlap),
// ~53° wide, slightly below eye level, following the head's YAW only through a
// dead zone (not head-locked: pitch/roll of the head never drag it along).
// Tunables live in client/xr-settings.js (Settings → XR in the HUD, saved per browser, URL
// params for a trial): width (default 2.2 m → 73° of view; user 2026-10-10: "bigger interface,
// head movement to study it"), drop below eye level, distance, dead zone, layout px, text edge.
const xrWidth = () => xrSettings.get('width');
const xrDrop = () => xrSettings.get('drop');
const xrDistance = () => xrSettings.get('distance');
// Lazy follow (user 2026-10-10, after measuring: readable zone ≈ the central 48°): the dead
// zone is HALF THE PANEL — while any part of the panel is in front of you nothing moves, so
// its edges can be read by turning the head; past the edge the gaze pushes it by the excess
// only (no jump, never "stands mid-scene" — the earlier zone moved it fully once left, 11:04).
// NO recentring at all (user 2026-10-10 19:20: "the HUD is still immediately reacting to my
// head changes" — recentring while turning WAS following every turn). `?hudzone=<deg>`.
// Position: a head TURN moves the eyes ~10 cm around the neck, so a 2 cm move dead zone made
// the panel shift on every turn too — only a real move (> 0.3 m) drags it along.
const xrDeadZone = () => xrSettings.deadZoneRad();
const XR_GUI_MOVE_DEADZONE = 0.3;   // m — a real step, not the head swinging around the neck
const XR_GUI_FOLLOW = 0.3;          // per-frame easing (exponential lag)
// Depth-adaptive panel (user 2026-10-10: "the HUD in front of an up-close object makes me
// want to vomit, my brain does not know what to focus"): the panel is never drawn BEHIND
// something nearer — it moves in front of the closest planet along its own directions
// (centre + both edges), scaled with the distance so it looks exactly the same size, so the
// eyes converge on panel and planet at nearly the same depth. Auto-hiding was rejected
// ("I might want to check a message while looking at my home planet").
const xrMinDistance = () => xrSettings.get('minDistance'); // m — never closer than this to the eyes
const XR_GUI_DEPTH_MARGIN = 0.12;   // m — in front of the surface
const XR_GUI_DEPTH_EASE = 0.12;     // per-frame easing of the depth change
// In VR the HUD is laid out for a SMALLER virtual viewport: buttons and fonts keep
// their px sizes, the panel holds fewer px → everything bigger on the same 1.5 m panel.
// At the Pico's 1280 px window layout a 16 px label is 0.7° tall — unreadable (user,
// 2026-10-10: "I can't read anything except the title and X 0 Y 0 GO"); 700 px on a 1.5 m panel
// → 1.31° (640 = 1.43° but the LCARS top bar's three groups collide on a 600 px run, measured).
// The layout width scales with the panel width so the px→metre scale (text size) stays.
// `?xrlayout=<px>` overrides for trials, `?xrlayout=0` = window layout.
const xrLayoutWidth = () => xrSettings.layoutWidth();
// Curved panel (client/curved-hud.js): the flat layout is drawn into a texture and
// shown on the inside of a cylinder of radius xrDistance() around the viewer.
const XR_GUI_CURVED = false; // 2026-10-10: user saw regressions in VR with it on; off until verified in VR one by one
const xrTextEdge = () => xrSettings.get('textEdge');    // text anti-aliasing ramp in VR (screen px; 1 = desktop). MSAA does not smooth
                             // texture-alpha edges, a 1 px ramp shimmers with head motion (Pico 2026-10-10)
// DEV: ?curvedhud=1 shows the curved panel in 2D too (in front of the camera) for screenshots
const DEV_CURVED_2D = typeof location !== 'undefined' && new URLSearchParams(location.search).has('curvedhud');
const _yAxis = new Vector3(0, 1, 0);
const _xrFwd = new Vector3(), _xrAnchor = new Vector3(), _xrYawQuat = new Quaternion();
const _xrHead = new Vector3(), _xrDir = new Vector3(), _xrToC = new Vector3(), _xrSideQuat = new Quaternion();
let xrGuiDepthK = 1; // current panel distance as a fraction of xrDistance()

// nearest planet surface along the panel's centre/left/right directions (world units = m)
function nearestSurfaceAhead(head, yawQuat, rigQuat, spheres) {
	let nearest = Infinity;
	const half = Math.atan(xrWidth() / 2 / xrDistance());
	for (const side of [0, -half, half]) {
		_xrSideQuat.setFromAxisAngle(_yAxis, side).premultiply(yawQuat);
		_xrDir.set(0, 0, -1).applyQuaternion(_xrSideQuat).applyQuaternion(rigQuat);
		for (const s of spheres) {
			_xrToC.copy(s.c).sub(head);
			const t = _xrToC.dot(_xrDir);
			if (t <= 0) continue;
			const perp2 = _xrToC.lengthSq() - t * t;
			if (perp2 >= s.r * s.r) continue;
			const entry = t - Math.sqrt(s.r * s.r - perp2);
			if (entry < nearest) nearest = entry;
		}
	}
	return nearest;
}

const XR_NEBULA_BOOST = false;

export default async function createGame() {
	let width = window.innerWidth;
	let height = window.innerHeight;
	const renderer = new WebGLRenderer({
		alpha: true, // make canvas background transparent
		antialias: true, // smooth edges
		stencil: true, // for overflow hidden
	});
	renderer.setClearColor(0x000000, 0);
	//renderer.useLegacyLights = true;
	renderer.outputColorSpace = LinearSRGBColorSpace;
	renderer.setPixelRatio(window.devicePixelRatio);
	renderer.setSize(width, height);
	renderer.shadowMap.enabled = !renderer.shadowMap.enabled;

	const canvas = renderer.domElement;
	document.body.appendChild(renderer.domElement);

	const scene = new Scene();
	const main = new Group();
	scene.add(main);
	const labels = new Group();
	labels.name = 'LABELS';
	labels.renderOrder = 1000;
	main.add(labels);
	const gui = new Group();
	gui.name = 'GUI';
	gui.renderOrder = 10000;
	const raycaster = new Raycaster();
	const mouse = new Vector2();
	canvas.addEventListener(
		'pointermove',
		event => {
			// calculate mouse position in normalized device coordinates
			// (-1 to +1) for both components
			//l(event);
			mouse.event = event;
			mouse.x = (event.clientX / canvas.clientWidth) * 2 - 1;
			mouse.y = -(event.clientY / canvas.clientHeight) * 2 + 1;
		},
		false,
	);
	const camera = new PerspectiveCamera(
		50,
		canvas.clientWidth / canvas.clientHeight,
		0.1 /*if this value has too many digits after comma, flickering starts because to big values*/,
		999999,
	);
	camera.position.set(0, 2000, 1000);
	camera.name = 'DefaultMap';
	const cameraRig = new Group();
	cameraRig.name = 'CAMERA_RIG';
	scene.add(cameraRig);
	cameraRig.add(camera);
	camera.add(gui);

	const audioListenerPhaser = new AudioListener();
	const audioListenerExplosion = new AudioListener();
	camera.add(audioListenerPhaser);
	camera.add(audioListenerExplosion);

	const controls = new MapControls(camera, renderer.domElement);
	controls.maxDistance = 5000;
	controls.minDistance = 1;
	//controls.minAzimuthAngle = 0;
	//controls.maxAzimuthAngle = window.innerWidth / window.innerHeight;
	//controls.minPolarAngle = 0;
	controls.maxPolarAngle = Infinity; //Math.PI / 4;

	//controls.enableDamping = true; // an animation loop is required when either damping or auto-rotation are enabled
	//controls.dampingFactor = .1;
	controls.rotateSpeed = 0.5;
	controls.zoomSpeed = 4;
	//controls.target.set(0, -175, 0);
	controls.zoomToCursor = true;
	// two-finger touch = pinch-zoom + rotate: spreading zooms, moving together rotates (user
	// 2026-10-12). DOLLY_ROTATE alone rotated from the midpoint drift of every uneven pinch, so
	// sector-map's touchTwoFingerGate classifies each gesture at its start and enables rotation
	// only for a drag. One finger pans.
	controls.touches.TWO = TOUCH.DOLLY_ROTATE;
	//controls.update();

	function updateTextureRepeat() {
		if (scene.background?.image) {
			const widthRepeat = canvas.clientWidth / scene.background.image.width;
			const heightRepeat = canvas.clientHeight / scene.background.image.height;
			scene.background.repeat.set(widthRepeat, heightRepeat);
		}
	}

	function setCanvasBackgroundTexture(file, width, height) {
		if (scene.userData.spaceBackground) {
			scene.userData.spaceBackground.parent?.remove(scene.userData.spaceBackground);
			disposeHierarchy(scene.userData.spaceBackground);
			scene.userData.spaceBackground = null;
		}
		const texture = textureLoader.load(file, () => {
			updateTextureRepeat();
		});
		texture.wrapS = RepeatWrapping;
		texture.wrapT = RepeatWrapping;
		texture.repeat.set(
			canvas.clientWidth / width,
			canvas.clientHeight / height,
		);
		scene.background = texture;
	}

	function setSpaceBackground() {
		if (scene.userData.spaceBackground) {
			scene.userData.spaceBackground.parent?.remove(scene.userData.spaceBackground);
			disposeHierarchy(scene.userData.spaceBackground);
		}
		scene.background = new Color(0x01020a);
		const background = createSpaceBackground();
		scene.userData.spaceBackground = background;
		main.add(background);
	}

	const frameCallbacks = [];

	// GUI positioned so 1 unit = 1 pixel
	function getGuiDistance() {
		const vFov = camera.fov * Math.PI / 180;
		return height / (2 * Math.tan(vFov / 2));
	}

	// VR HUD placement (see XR_GUI_* above). In VR the gui is a child of the RIG,
	// not the camera: a camera child recomputed from the previous frame's head pose
	// shook with every head tracking update (user, 2026-10-10). Anchored to the rig
	// it only moves when the gaze/head leave their dead zones (eased), and it flies
	// along with the rig. All poses are rig-local; hit-testing uses world matrices.
	let xrGuiYaw = null;
	let guiLayoutWidth = width;
	let guiLayoutHeight = height;
	const curvedHud = new CurvedHud({ renderer, gui });
	// re-lay the HUD roots out for VR (virtual viewport) or back for 2D (window size)
	function relayoutGuiFor(xr) {
		// virtual viewport keeps the window's aspect so the panel's proportions do not change
		const lay = xr && xrLayoutWidth() > 0 ? { width: xrLayoutWidth(), height: xrSettings.layoutHeight(window.innerWidth, window.innerHeight) } : null;
		const w = lay ? lay.width : window.innerWidth, h = lay ? lay.height : window.innerHeight;
		globalThis.__guiViewport = lay; // box-patcher / hud-lcars / keyboard honour it on rebuilds
		const roots = [];
		for (const c of gui.children) {
			if (c.isBox && c.layoutProps) roots.push(c);
			else for (const cc of c.children || []) if (cc.isBox && cc.layoutProps) roots.push(cc);
		}
		for (const box of roots) {
			if (typeof box.setSize !== 'function' || typeof box.layout !== 'function') continue;
			if (!box.userData.viewportInset) box.userData.viewportInset = [window.innerWidth - box.layoutProps.width, window.innerHeight - box.layoutProps.height];
			const [iw, ih] = box.userData.viewportInset;
			box.layoutProps.width = w - iw;
			box.layoutProps.height = h - ih;
			box.setSize(box.layoutProps.width, box.layoutProps.height);
			box.layout();
		}
		// the LCARS frame (imperial-lcars LcarsBox): LcarsBox#update re-lays it out in place at the new viewport
		// (hud-lcars.js reads globalThis.__guiViewport → width/height/fontSize); a frame that is missing is rebuilt in full
		const lcars = gui.children.filter(c => typeof c._build === 'function' && typeof c.dispose === 'function');
		if (lcars.length || game.lcarsHud) { // a frame that got lost (rebuild failed) is rebuilt too, never left missing
			import('./hud-lcars.js').then(m => m.updateLcarsHud()).catch(e => console.error('[lcars-hud] relayout failed', e));
		}
		guiLayoutWidth = w;
		guiLayoutHeight = h;
		// text edge softness: new texts via the library default, existing ones in place
		// desktop 0.5: imperial-text-base screenPxRange() assumes a two-sided pxRange but sdf-atlas.js ships the one-sided spread,
		// so the SDF ramp is ~2x wide at edge 1 (crispness 0.658 -> 0.79 at 0.5; LOG.md 2026-10-21 text blur). VR keeps its own value.
		const edge = xr ? (globalThis.__textEdge ?? xrTextEdge()) : 0.5;
		setTextEdge(edge);
		scene.traverse(o => { const u = o.material?.uniforms; if (u?.edge) u.edge.value = edge; });
	}
	// Settings → XR changes that alter the layout or the text shader apply at once
	xrSettings.onChange((key) => {
		if (!renderer.xr.isPresenting) return;
		if (key === 'width' || key === 'height' || key === 'layout' || key === 'textEdge') relayoutGuiFor(true);
	});
	renderer.xr.addEventListener('sessionstart', () => relayoutGuiFor(true));
	renderer.xr.addEventListener('sessionend', () => relayoutGuiFor(false));
	setTextEdge(0.5); // desktop start value for texts built before any relayout (same cause as in relayoutGuiFor)
	function updateXRGui() {
		if (gui.parent !== cameraRig) cameraRig.add(gui);
		_xrFwd.set(0, 0, -1).applyQuaternion(camera.quaternion); // head forward, rig-local
		const headYaw = Math.atan2(-_xrFwd.x, -_xrFwd.z);
		if (xrGuiYaw === null) { xrGuiYaw = headYaw; _xrAnchor.copy(camera.position); }
		let d = headYaw - xrGuiYaw;
		d = Math.atan2(Math.sin(d), Math.cos(d));
		if (Math.abs(d) > xrDeadZone()) xrGuiYaw += (d - Math.sign(d) * xrDeadZone()) * XR_GUI_FOLLOW;
		if (_xrAnchor.distanceTo(camera.position) > XR_GUI_MOVE_DEADZONE) _xrAnchor.lerp(camera.position, XR_GUI_FOLLOW);
		_xrYawQuat.setFromAxisAngle(_yAxis, xrGuiYaw);
		// depth: in front of the nearest planet surface ahead, same angular size
		camera.getWorldPosition(_xrHead);
		const ahead = nearestSurfaceAhead(_xrHead, _xrYawQuat, cameraRig.quaternion, game.sectorMap?._labelSpheres || []);
		const wantK = Math.max(xrMinDistance(), Math.min(xrDistance(), ahead - XR_GUI_DEPTH_MARGIN)) / xrDistance();
		xrGuiDepthK += (wantK - xrGuiDepthK) * XR_GUI_DEPTH_EASE;
		const k = xrGuiDepthK;
		gui.position.set(0, -xrDrop() * k, -xrDistance() * k).applyQuaternion(_xrYawQuat).add(_xrAnchor);
		gui.quaternion.copy(_xrYawQuat);
		// scale ∝ the panel centre's distance from the HEAD (camera, rig-local), not from the lagging anchor:
		// the apparent angular width stays xrWidth()/xrDistance() at every depth (was 59° vs 70° up close,
		// 2026-10-21; the anchor trails the head by up to XR_GUI_MOVE_DEADZONE, huge at 0.5 m)
		const headDist = Math.max(0.05, gui.position.distanceTo(camera.position));
		gui.scale.setScalar(xrWidth() * (headDist / xrDistance()) / guiLayoutWidth); // layout px → metres
		if (XR_GUI_CURVED) showCurved(cameraRig, _xrAnchor, _xrYawQuat);
	}

	// the curved panel: cylinder axis through the anchor (the eyes), centre dropped like
	// the flat panel; the flat gui stays where it is but hidden (texture source + ray target)
	function showCurved(parent, anchor, yawQuat, k = 1) {
		curvedHud.configure({ layoutWidth: guiLayoutWidth, layoutHeight: guiLayoutHeight, widthMetres: xrWidth() * k, radius: xrDistance() * k });
		const m = curvedHud.mesh;
		if (m.parent !== parent) parent.add(m);
		m.position.copy(anchor); m.position.y -= xrDrop() * k;
		m.quaternion.copy(yawQuat);
		m.visible = true;
		curvedHud.enabled = true;
		curvedHud.patchHitTester(game._input && game._input.hitTester);
		curvedHud.render();
		gui.visible = false;
	}
	function hideCurved() {
		if (curvedHud.mesh) curvedHud.mesh.visible = false;
		curvedHud.enabled = false;
		gui.visible = true;
	}

	function animationLoop() {
		// three's Sprite.raycast reads raycaster.camera; in VR the ray comes from the controller
		// (web-xr sets origin/direction) and setFromCamera never runs, so a hover over a unit with
		// its thruster glow (a Sprite) threw inside this loop and the XR loop died — "the ray froze"
		// (user 2026-10-12). The head camera is the right view for a sprite's billboard test.
		raycaster.camera = camera;
		if (!renderer.xr.isPresenting) {
			raycaster.setFromCamera(mouse, camera);
			if (gui.parent !== camera) camera.add(gui);
			gui.position.set(0, 0, -getGuiDistance());
			gui.quaternion.identity();
			gui.scale.setScalar(1);
			xrGuiYaw = null;
			if (DEV_CURVED_2D) { // screenshot check of the curved renderer: panel as in VR, around the 2D camera
				// the 2D camera's near plane is far larger than 1.5 units → scale the whole setup to it
				const k = Math.max(1, camera.near * 4 / xrDistance());
				gui.position.set(0, -xrDrop() * k, -xrDistance() * k); gui.scale.setScalar(xrWidth() * k / guiLayoutWidth);
				_xrAnchor.set(0, 0, 0); _xrYawQuat.identity(); showCurved(camera, _xrAnchor, _xrYawQuat, k);
			} else hideCurved();
		} else {
			updateXRGui();
		}
		const background = scene.userData.spaceBackground;
		if (background) {
			// #60: the headset shows the SAME nebula as the desktop (measured 2026-10-26: the XR-only planes with opacity x1.45 were ~3.5x brighter blue, the neon veil). Set true for the old XR nebula.
			const xrBoost = XR_NEBULA_BOOST && renderer.xr.isPresenting;
			background.traverse(child => {
				if (!child.userData.spaceNebula) return;
				child.visible = xrBoost
					? child.userData.nebulaMode === 'xr'
					: child.userData.nebulaMode !== 'xr';
				child.material.opacity = child.userData.baseOpacity * (xrBoost ? 1.45 : 1);
				child.material.color.setHex(xrBoost ? 0xa8cfff : 0x91bdff);
			});
		}
		for (let i = 0; i < frameCallbacks.length; i++) frameCallbacks[i]();
		renderer.render(scene, camera);
		// after the render: in XR the camera now carries the headset pose
		captureIfRequested(renderer, scene, camera);
	}
	renderer.setAnimationLoop(animationLoop);

	let resizeTimer = null;
	window.onresize = event => {
		width = window.innerWidth;
		height = window.innerHeight;
		renderer.setSize(width, height);
		camera.aspect = width / height;
		camera.updateProjectionMatrix();
		updateTextureRepeat();
		// the HUD follows the window (user 2026-10-12: "when i resize the browser window the hud
		// is static"): re-lay the roots out and rebuild the LCARS frame, once the drag settles
		if (!renderer.xr.isPresenting) {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => relayoutGuiFor(false), 250);
		}
	};

	const gameApi = {
		renderer,
		scene,
		main,
		labels,
		gui,
		camera,
		cameraRig,
		audioListenerPhaser,
		audioListenerExplosion,
		controls,
		raycaster,
		container: canvas,
		setCanvasBackgroundTexture,
		setSpaceBackground,
		// a touch tap has no pointermove before it: the map sets the ray from the tap position
		setPointer(clientX, clientY) {
			mouse.x = (clientX / canvas.clientWidth) * 2 - 1;
			mouse.y = -(clientY / canvas.clientHeight) * 2 + 1;
			raycaster.setFromCamera(mouse, camera);
		},
		onFrame(fn) { frameCallbacks.push(fn); },
		offFrame(fn) { const i = frameCallbacks.indexOf(fn); if (i >= 0) frameCallbacks.splice(i, 1); },
		captureScreenshot: requestScreenshot, // HUD 'Shot' button + console; see client/screenshot.js
		recenterHud() { xrGuiYaw = null; }, // the lazy-following HUD re-centres on the head (after a camera focus, client/focus.js)
		fovText: { show: showFovText, hide: hideFovText }, // DEV: HUD → panel-filling text (client/fov-text.js)
		curvedHud, // VR curved panel (debug/tests)
	};
	audioLoader.load('/assets/sounds/phaser.mp3', buffer => {
		gameApi.audioBufferPhaser = buffer;
	});
	audioLoader.load('/assets/sounds/explosion.mp3', buffer => {
		gameApi.audioBufferExplosion = buffer;
	});
	return gameApi;
}

export function disposeHierarchy(node, self = true) {
	for (var i = node.children.length - 1; i >= 0; i--) {
		const child = node.children[i];
		disposeHierarchy(child);
		disposeNode(child);
	}
	self && disposeNode(node);
}

export function disposeNode(node) {
	if (node.$persistant) {
		return;
	}
	node.geometry && node.geometry.dispose();
	/*if (node.material) {
		if (Array.isArray(node.material)) {
			node.material.forEach(m => disposeNodeMaterial);
		} else {
			disposeNodeMaterial(node.material);
		}
	}*/
	node.material && node.material.dispose();
	node.parent && node.parent.remove(node);
}

export function disposeNodeMaterial(m) {
	if (m.map) m.map.dispose();
	if (m.lightMap) m.lightMap.dispose();
	if (m.bumpMap) m.bumpMap.dispose();
	if (m.normalMap) m.normalMap.dispose();
	if (m.specularMap) m.specularMap.dispose();
	if (m.envMap) m.envMap.dispose();
	if (m.alphaMap) m.alphaMap.dispose();
	if (m.aoMap) m.aoMap.dispose();
	if (m.displacementMap) m.displacementMap.dispose();
	if (m.emissiveMap) m.emissiveMap.dispose();
	if (m.gradientMap) m.gradientMap.dispose();
	if (m.metalnessMap) m.metalnessMap.dispose();
	if (m.roughnessMap) m.roughnessMap.dispose();
	m.dispose(); // disposes any programs associated with the material
}
